<template>
  <div class="templates-container">
    <!-- Header with Add Button -->
    <div class="d-flex align-center mb-4">
      <h2 class="text-h5">قوالب الرسائل</h2>
      <v-spacer></v-spacer>
      <v-btn
        v-if="hasPermission('admin.mail-templates.store')"
        color="primary"
        prepend-icon="mdi-plus"
        @click="openAddDialog"
      >
        إضافة قالب جديد
      </v-btn>
    </div>
    
    <!-- Search and Filter -->
    <v-card class="mb-4" flat>
      <v-card-text>
        <v-row align="center">
          <v-col cols="12" sm="6">
            <v-text-field
              v-model="search"
              prepend-inner-icon="mdi-magnify"
              label="بحث"
              hide-details
              density="compact"
              variant="outlined"
              :clearable="true"
              clear-icon="mdi-close"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6">
            <v-select
              v-model="categoryFilter"
              :items="categories"
              label="الفئة"
              item-title="name"
              item-value="id"
              hide-details
              density="compact"
              variant="outlined"
              :clearable="true"
              clear-icon="mdi-close"
            ></v-select>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
    
    <!-- Templates Table -->
    <v-card>
      <v-data-table
        :headers="headers"
        :items="filteredTemplates"
        :loading="loading"
        color="primary"
        first-icon="mdi-chevron-left"
        last-icon="mdi-chevron-right"
        next-icon="mdi-chevron-right"
        prev-icon="mdi-chevron-left"
        show-current-page
        show-first-last-page
        show-first-page
        show-last-page
        show-next-page
        show-previous-page
        class="elevation-1"
      >
        <template #[`item.name`]="{ item }">
          <div>
            <div class="font-weight-medium">{{ item.name }}</div>
            <div class="text-caption text-grey">{{ item.desc }}</div>
          </div>
        </template>
        
        <template #[`item.category`]="{ item }">
          <v-chip
            :color="getCategoryColor(item.mail_template_category_id)"
            text-color="white"
            size="small"
          >
            {{ getCategoryName(item.mail_template_category_id) }}
          </v-chip>
        </template>
        
        <template #[`item.content`]="{ item }">
          <div class="text-truncate" style="max-width: 200px;">
            {{ stripHtml(item.content) }}
          </div>
        </template>
        
        <template #[`item.created_at`]="{ item }">
          {{ formatDate(item.created_at) }}
        </template>
        
        <template #[`item.is_private`]="{ item }">
          <v-chip
            :color="item.is_private ? 'success' : 'info'"
            text-color="white"
            size="small"
          >
            {{ item.is_private ? 'خاص' : 'عام' }}
          </v-chip>
        </template>
        
        <template #[`item.actions`]="{ item }">
          <v-btn
            v-if="hasPermission('admin.mail-templates.update')"
            icon
            variant="text"
            size="small"
            color="primary"
            @click="editTemplate(item)"
            title="تعديل"
          >
            <v-icon>mdi-pencil</v-icon>
          </v-btn>
          <v-btn
            v-if="hasPermission('admin.mail-templates.show')"
            icon
            variant="text"
            size="small"
            color="info"
            @click="previewTemplate(item)"
            title="معاينة"
          >
            <v-icon>mdi-eye</v-icon>
          </v-btn>
          <v-btn
            v-if="hasPermission('admin.mail-templates.destroy')"
            icon
            variant="text"
            size="small"
            color="error"
            @click="deleteTemplate(item)"
            title="حذف"
          >
            <v-icon>mdi-delete</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card>
    
    <!-- Add/Edit Dialog -->
    <v-dialog v-model="dialog" max-width="1000px">
      <v-card>
        <v-card-title>
          <span>{{ isEditing ? 'تعديل قالب' : 'إضافة قالب جديد' }}</span>
        </v-card-title>
        
        <v-card-text>
          <v-form ref="form" v-model="valid">
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="editedItem.name"
                  label="اسم القالب"
                  :rules="[v => !!v || 'اسم القالب مطلوب']"
                  required
                ></v-text-field>
              </v-col>
              
              <v-col cols="12" md="6">
                <v-select
                  v-model="editedItem.mail_template_category_id"
                  :items="categories"
                  label="الفئة"
                  item-title="name"
                  item-value="id"
                  :rules="[v => !!v || 'الفئة مطلوبة']"
                  required
                ></v-select>
              </v-col>
              
              <v-col cols="12">
                <v-textarea
                  v-model="editedItem.desc"
                  label="وصف القالب"
                  rows="2"
                  :rules="[v => !!v || 'وصف القالب مطلوب']"
                  required
                ></v-textarea>
              </v-col>
              
              <v-col cols="12">
                <v-text-field
                  v-model="editedItem.subject"
                  label="موضوع الرسالة"
                  :rules="[v => !!v || 'موضوع الرسالة مطلوب']"
                  required
                ></v-text-field>
              </v-col>
              
              
              <v-col cols="12">
                <v-switch
                  v-model="editedItem.is_private"
                  label="قالب خاص"
                  color="primary"
                  hide-details
                ></v-switch>
              </v-col>
              
              <v-col cols="12">
                <label class="text-subtitle-2 mb-2 d-block">محتوى الرسالة</label>
                
                <!-- Quill Rich Text Editor -->
                <QuillEditor
                  ref="quillEditor"
                  v-model:content="editedItem.content"
                  :options="editorOptions"
                  @textChange="onEditorChange"
                  class="quill-editor-container"
                  contentType="html"
                />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            variant="text"
            @click="save"
          >
            حفظ
          </v-btn>
          <v-btn
            color="error"
            variant="text"
            @click="close"
          >
            إلغاء
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    
    <!-- Preview Dialog -->
    <v-dialog v-model="previewDialog" max-width="900px">
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon class="mr-2">mdi-email-outline</v-icon>
          <span>معاينة القالب</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="previewDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        
        <v-card-text>
          <!-- Email Preview Layout -->
          <div class="email-preview-container">
            <div class="email-header">
              <v-row class="mb-3">
                <v-col cols="12">
                  <div class="email-field">
                    <strong>من:</strong> النظام
                  </div>
                </v-col>
                <v-col cols="12">
                  <div class="email-field">
                    <strong>إلى:</strong> المستخدم
                  </div>
                </v-col>
                <v-col cols="12">
                  <div class="email-field">
                    <strong>الموضوع:</strong> {{ previewTemplate2.subject }}
                  </div>
                </v-col>
                <v-col cols="12">
                  <div class="email-field">
                    <strong>التاريخ:</strong> {{ new Date().toLocaleDateString('ar-LY') }}
                  </div>
                </v-col>
              </v-row>
            </div>
            
            <v-divider class="my-4"></v-divider>
            
            <!-- Email Content with Rich Text Styling -->
            <div class="email-content ql-editor" v-html="previewTemplate2.content"></div>
          </div>
        </v-card-text>
        
        <v-card-actions>
          <v-btn
            color="primary"
            prepend-icon="mdi-printer"
            variant="outlined"
            @click="printPreview"
          >
            طباعة
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            @click="previewDialog = false"
          >
            إغلاق
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    
    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400px">
      <v-card>
        <v-card-title>تأكيد الحذف</v-card-title>
        <v-card-text>
          هل أنت متأكد من حذف القالب "{{ itemToDelete?.name }}"؟
          <br>
          <small class="text-grey">لا يمكن التراجع عن هذا الإجراء.</small>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="error"
            variant="text"
            @click="confirmDelete"
          >
            حذف
          </v-btn>
          <v-btn
            color="primary"
            variant="text"
            @click="deleteDialog = false"
          >
            إلغاء
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import mailTemplateService from '@/services/mailTemplateService'
import mailTemplateCategoryService from '@/services/mailTemplateCategoryService'
import { QuillEditor } from '@vueup/vue-quill'
import '@vueup/vue-quill/dist/vue-quill.snow.css'
import { hasPermission, hasModule } from '@/utils/permissions'
export default {
  name: 'TemplatesView',
  components: {
    QuillEditor
  },
  data() {
    return {
      search: '',
      categoryFilter: null,
      loading: false,
      dialog: false,
      previewDialog: false,
      deleteDialog: false,
      valid: true,
      isEditing: false,
      headers: [
        { title: 'اسم القالب', key: 'name', align: 'start' },
        { title: 'الفئة', key: 'category' },
        { title: 'الموضوع', key: 'subject' },
        { title: 'المحتوى', key: 'content' },
        { title: 'تاريخ الإنشاء', key: 'created_at' },
        { title: 'نوع القالب', key: 'is_private', align: 'center' },
        { title: 'الإجراءات', key: 'actions', sortable: false, width: '150px' }
      ],
      categories: [],
      templates: [],
      editedItem: {
        id: null,
        name: '',
        desc: '',
        mail_template_category_id: null,
        subject: '',
        content: '',
        is_private: false
      },
      defaultItem: {
        id: null,
        name: '',
        desc: '',
        mail_template_category_id: null,
        subject: '',
        content: '',
        is_private: false
      },
      previewTemplate2: {},
      itemToDelete: null,
      // Quill Editor Configuration
      editorOptions: {
        theme: 'snow',
        placeholder: 'اكتب محتوى الرسالة هنا...',
        modules: {
          toolbar: [
            [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
            [{ 'size': ['small', false, 'large', 'huge'] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'font': [] }],
            [{ 'align': [] }],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            [{ 'indent': '-1'}, { 'indent': '+1' }],
            ['blockquote', 'code-block'],
            ['link', 'image'],
            ['clean']
          ]
        }
      }
    }
  },

  async mounted() {
    await this.loadTemplates()
    await this.loadCategories()
  },

  computed: {
    filteredTemplates() {
      let result = [...this.templates];
      
      if (this.search) {
        const searchLower = this.search.toLowerCase();
        result = result.filter(t => 
          t.name.toLowerCase().includes(searchLower) ||
          t.desc.toLowerCase().includes(searchLower) ||
          t.subject.toLowerCase().includes(searchLower)
        );
      }
      
      if (this.categoryFilter) {
        result = result.filter(t => t.mail_template_category_id === this.categoryFilter);
      }
      
      return result;
    }
  },
  methods: {
    hasPermission(permission) {
      // Prevent permission checks during logout to avoid DOM errors
      if (this.$store.state.isLoggingOut) {
        return false
      }
      return hasPermission(permission)
    },
    
    hasModule(moduleName) {
      // Prevent permission checks during logout to avoid DOM errors
      if (this.$store.state.isLoggingOut) {
        return false
      }
      return hasModule(moduleName)
    },
    // Load templates from API
    async loadTemplates() {
      this.loading = true
      try {
        const response = await mailTemplateService.getMailTemplates()
        //console.log('Templates API response:', response)
        
        // Handle paginated response - the actual data is nested deeper
        if (response.data && response.data.data && response.data.data.data && Array.isArray(response.data.data.data)) {
          this.templates = response.data.data.data
        } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
          this.templates = response.data.data
        } else if (response.data && Array.isArray(response.data)) {
          this.templates = response.data
        } else {
          this.templates = []
        }
        
        // Add is_private property based on department_id logic
        this.templates = this.templates.map(template => ({
          ...template,
          is_private: template.department_id !== null
        }))
        
        //console.log('Loaded templates:', this.templates)
      } catch (error) {
        console.error('Error loading templates:', error)
        this.$store.dispatch('alerts/showAlert', {
          id: Date.now(),
          type: 'error',
          color: 'error',
          icon: 'mdi-alert-circle',
          timeout: 3000,
          closable: true,
          message: error.message || 'فشل في جلب القوالب'
        })
      } finally {
        this.loading = false
      }
    },

    // Load categories from API
    async loadCategories() {
      try {
        const response = await mailTemplateCategoryService.getMailTemplateCategories()
        //console.log('Categories API response:', response)
        
        // Handle paginated response - the actual data is nested deeper
        if (response.data && response.data.data && response.data.data.data && Array.isArray(response.data.data.data)) {
          this.categories = response.data.data.data
        } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
          this.categories = response.data.data
        } else if (response.data && Array.isArray(response.data)) {
          this.categories = response.data
        } else {
          this.categories = []
        }
        //console.log('Loaded categories:', this.categories)
      } catch (error) {
        console.error('Error loading categories:', error)
        this.$store.dispatch('alerts/showAlert', {
          id: Date.now(),
          type: 'error',
          color: 'error',
          icon: 'mdi-alert-circle',
          timeout: 3000,
          closable: true,
          message: error.message || 'فشل في جلب فئات القوالب'
        })
      }
    },

    openAddDialog() {
      this.isEditing = false;
      this.editedItem = { ...this.defaultItem };
      this.dialog = true;
    },
    editTemplate(item) {
      this.isEditing = true;
      this.editedItem = { 
        ...item,
        is_private: item.department_id !== null
      };
      this.dialog = true;
    },
    previewTemplate(item) {
      this.previewTemplate2 = { ...item };
      this.previewDialog = true;
    },
    deleteTemplate(item) {
      this.itemToDelete = item;
      this.deleteDialog = true;
    },
    async confirmDelete() {
      try {
        await mailTemplateService.deleteMailTemplate(this.itemToDelete.id)
        await this.loadTemplates()
        this.$store.dispatch('alerts/showAlert', {
          id: Date.now(),
          type: 'success',
          color: 'success',
          icon: 'mdi-check-circle',
          timeout: 3000,
          closable: true,
          message: 'تم حذف القالب بنجاح'
        })
        this.deleteDialog = false
        this.itemToDelete = null
      } catch (error) {
        this.$store.dispatch('alerts/showAlert', {
          id: Date.now(),
          type: 'error',
          color: 'error',
          icon: 'mdi-alert-circle',
          timeout: 3000,
          closable: true,
          message: error.message || 'حدث خطأ أثناء حذف القالب'
        })
      }
    },
    getCategoryName(categoryId) {
      const category = this.categories.find(c => c.id === categoryId);
      return category ? category.name : '';
    },
    getCategoryColor(categoryId) {
      const colors = ['primary', 'info', 'warning', 'success', 'error', 'purple', 'teal', 'orange'];
      const index = categoryId ? categoryId % colors.length : 0;
      return colors[index];
    },
    stripHtml(html) {
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      return tmp.textContent || tmp.innerText || '';
    },
    formatDate(dateString) {
      if (!dateString) return '';
      return new Date(dateString).toLocaleDateString('ar-LY', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    },
    // Quill Editor Methods
    onEditorChange() {
      // Content is automatically updated through v-model
      // This method can be used for additional logic if needed
    },
    async save() {
      if (!this.$refs.form.validate()) return;
      
      try {
        // Prepare data according to API specification
        const templateData = {
          name: this.editedItem.name,
          mail_template_category_id: this.editedItem.mail_template_category_id,
          desc: this.editedItem.desc,
          subject: this.editedItem.subject,
          content: this.editedItem.content,
          is_private: this.editedItem.is_private
        };

        if (this.isEditing) {
          await mailTemplateService.updateMailTemplate(this.editedItem.id, templateData)
        } else {
          await mailTemplateService.createMailTemplate(templateData)
        }
        
        await this.loadTemplates()
        this.close()
        this.$store.dispatch('alerts/showAlert', {
          id: Date.now(),
          type: 'success',
          color: 'success',
          icon: 'mdi-check-circle',
          timeout: 3000,
          closable: true,
          message: this.isEditing ? 'تم تحديث القالب بنجاح' : 'تم إضافة القالب بنجاح'
        })
      } catch (error) {
        this.$store.dispatch('alerts/showAlert', {
          id: Date.now(),
          type: 'error',
          color: 'error',
          icon: 'mdi-alert-circle',
          timeout: 3000,
          closable: true,
          message: error.message || 'حدث خطأ أثناء حفظ القالب'
        })
      }
    },
    close() {
      this.dialog = false;
      this.$nextTick(() => {
        this.editedItem = { ...this.defaultItem };
        this.$refs.form.reset();
      });
    },
    printPreview() {
      // Create a new window for printing
      const printWindow = window.open('', '_blank');
      const printContent = `
        <!DOCTYPE html>
        <html lang="ar" dir="rtl">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>${this.previewTemplate2.subject}</title>
          <style>
            body { 
              font-family: 'Arial', 'Tahoma', sans-serif; 
              margin: 20px; 
              direction: rtl; 
              line-height: 1.8;
              color: #333;
            }
            /* Quill Editor Styles for Print */
            .ql-editor p { margin: 0 0 1em 0; text-align: right; }
            .ql-editor h1 { font-size: 2em; font-weight: bold; margin: 1.5em 0 0.5em 0; color: #1976d2; }
            .ql-editor h2 { font-size: 1.5em; font-weight: bold; margin: 1.3em 0 0.5em 0; color: #1976d2; }
            .ql-editor h3 { font-size: 1.3em; font-weight: bold; margin: 1.2em 0 0.5em 0; color: #333; }
            .ql-editor h4, .ql-editor h5, .ql-editor h6 { 
              font-size: 1.1em; font-weight: bold; margin: 1em 0 0.5em 0; color: #333; 
            }
            .ql-editor ul, .ql-editor ol { margin: 1em 0; padding-right: 2em; padding-left: 0; }
            .ql-editor li { margin: 0.5em 0; text-align: right; }
            .ql-editor strong { font-weight: bold; color: #1976d2; }
            .ql-editor em { font-style: italic; color: #666; }
            .ql-editor u { text-decoration: underline; }
            .ql-editor a { color: #1976d2; text-decoration: underline; }
            .ql-editor blockquote { 
              border-right: 4px solid #1976d2; 
              border-left: none;
              margin: 1em 0; 
              padding: 0.5em 1em; 
              background: #f9f9f9; 
              font-style: italic;
              color: #555;
            }
            .ql-editor code {
              background-color: #f5f5f5;
              padding: 2px 4px;
              border-radius: 3px;
              font-family: 'Courier New', Courier, monospace;
              font-size: 0.9em;
            }
            .ql-editor pre {
              background-color: #f5f5f5;
              padding: 12px;
              border-radius: 4px;
              overflow-x: auto;
              margin: 1em 0;
            }
            .ql-editor img {
              max-width: 100%;
              height: auto;
              margin: 1em 0;
            }
            /* Text Alignment Classes */
            .ql-editor .ql-align-center { text-align: center; }
            .ql-editor .ql-align-left { text-align: left; }
            .ql-editor .ql-align-right { text-align: right; }
            .ql-editor .ql-align-justify { text-align: justify; }
            /* Font Size Classes */
            .ql-editor .ql-size-small { font-size: 0.75em; }
            .ql-editor .ql-size-large { font-size: 1.5em; }
            .ql-editor .ql-size-huge { font-size: 2.5em; }
            @media print {
              body { margin: 15mm; }
              .ql-editor { font-size: 12pt; line-height: 1.6; }
            }
          </style>
        </head>
        <body>
          <div class="ql-editor">${this.previewTemplate2.content}</div>
        </body>
        </html>
      `;
      
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();
      
      // Print after content loads
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 250);
    }
  }
}
</script>

<style scoped>
.templates-container {
  padding: 16px;
}

:deep(.v-data-table) {
  background-color: white;
}

:deep(.v-data-table-header) {
  background-color: #f5f5f5;
}

/* Quill Editor Styling */
.quill-editor-container {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background-color: white;
}

:deep(.ql-toolbar) {
  border-top: none;
  border-left: none;
  border-right: none;
  border-bottom: 1px solid #e0e0e0;
  background-color: #fafafa;
  border-radius: 4px 4px 0 0;
  padding: 8px;
}

:deep(.ql-container) {
  border: none;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  font-size: 14px;
  line-height: 1.6;
  border-radius: 0 0 4px 4px;
}

:deep(.ql-editor) {
  min-height: 250px;
  max-height: 400px;
  overflow-y: auto;
  padding: 16px;
}

:deep(.ql-editor.ql-blank::before) {
  font-style: normal;
  color: #999;
}

:deep(.ql-editor p) {
  margin: 0 0 1em 0;
}

:deep(.ql-editor ul, .ql-editor ol) {
  margin: 1em 0;
  padding-left: 2em;
}

:deep(.ql-editor li) {
  margin: 0.5em 0;
}

:deep(.ql-editor a) {
  color: #1976d2;
  text-decoration: underline;
}

:deep(.ql-editor a:hover) {
  text-decoration: none;
}

:deep(.ql-snow .ql-tooltip) {
  z-index: 1000;
}

:deep(.ql-snow .ql-picker-options) {
  z-index: 1000;
}

.border {
  border: 1px solid #e0e0e0;
}

/* Email Preview Styling */
.email-preview-container {
  background-color: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  max-height: 70vh;
  overflow-y: auto;
}

.email-header {
  background-color: #f8f9fa;
  padding: 16px;
  border-radius: 4px;
  margin-bottom: 16px;
}

.email-field {
  margin: 6px 0;
  font-size: 14px;
  color: #333;
}

.email-field strong {
  color: #1976d2;
  margin-left: 8px;
}

/* Email Content Rich Text Styling */
.email-content.ql-editor {
  border: none !important;
  padding: 20px !important;
  background-color: white;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  font-size: 14px;
  line-height: 1.8;
  color: #333;
  min-height: auto !important;
  max-height: none !important;
}

/* Enhanced Rich Text Styles for Preview */
:deep(.email-content.ql-editor p) {
  margin: 0 0 1em 0;
  text-align: right;
}

:deep(.email-content.ql-editor h1) {
  font-size: 2em;
  font-weight: bold;
  margin: 1.5em 0 0.5em 0;
  color: #1976d2;
}

:deep(.email-content.ql-editor h2) {
  font-size: 1.5em;
  font-weight: bold;
  margin: 1.3em 0 0.5em 0;
  color: #1976d2;
}

:deep(.email-content.ql-editor h3) {
  font-size: 1.3em;
  font-weight: bold;
  margin: 1.2em 0 0.5em 0;
  color: #333;
}

:deep(.email-content.ql-editor h4, .email-content.ql-editor h5, .email-content.ql-editor h6) {
  font-size: 1.1em;
  font-weight: bold;
  margin: 1em 0 0.5em 0;
  color: #333;
}

:deep(.email-content.ql-editor ul, .email-content.ql-editor ol) {
  margin: 1em 0;
  padding-right: 2em;
  padding-left: 0;
}

:deep(.email-content.ql-editor li) {
  margin: 0.5em 0;
  text-align: right;
}

:deep(.email-content.ql-editor strong) {
  font-weight: bold;
  color: #1976d2;
}

:deep(.email-content.ql-editor em) {
  font-style: italic;
  color: #666;
}

:deep(.email-content.ql-editor u) {
  text-decoration: underline;
}

:deep(.email-content.ql-editor a) {
  color: #1976d2;
  text-decoration: underline;
}

:deep(.email-content.ql-editor a:hover) {
  text-decoration: none;
  color: #1565c0;
}

:deep(.email-content.ql-editor blockquote) {
  border-right: 4px solid #1976d2;
  border-left: none;
  margin: 1em 0;
  padding: 0.5em 1em;
  background-color: #f5f5f5;
  font-style: italic;
  color: #555;
}

:deep(.email-content.ql-editor code) {
  background-color: #f5f5f5;
  padding: 2px 4px;
  border-radius: 3px;
  font-family: 'Courier New', Courier, monospace;
  font-size: 0.9em;
}

:deep(.email-content.ql-editor pre) {
  background-color: #f5f5f5;
  padding: 12px;
  border-radius: 4px;
  overflow-x: auto;
  margin: 1em 0;
}

:deep(.email-content.ql-editor img) {
  max-width: 100%;
  height: auto;
  border-radius: 4px;
  margin: 1em 0;
}

/* Text Alignment Classes */
:deep(.email-content.ql-editor .ql-align-center) {
  text-align: center;
}

:deep(.email-content.ql-editor .ql-align-left) {
  text-align: left;
}

:deep(.email-content.ql-editor .ql-align-right) {
  text-align: right;
}

:deep(.email-content.ql-editor .ql-align-justify) {
  text-align: justify;
}

/* Font Size Classes */
:deep(.email-content.ql-editor .ql-size-small) {
  font-size: 0.75em;
}

:deep(.email-content.ql-editor .ql-size-large) {
  font-size: 1.5em;
}

:deep(.email-content.ql-editor .ql-size-huge) {
  font-size: 2.5em;
}
</style> 